import { __assign } from "tslib";
import * as React from 'react';
import clsx from 'clsx';
import { makeStyles, createStyles } from '../../styles';
import { Listbox as BbUiListbox } from '../../Listbox';
import { useComboboxContext } from '../Combobox.context';
import { useListboxHandlers } from '../../Listbox/useListboxHandlers';
export var useStyles = makeStyles(function () {
    return createStyles({
        list: {
            margin: 0,
            padding: 0,
            '& > li > span:hover': {
                textDecoration: 'underline',
            },
        },
        listbox: {
            maxHeight: '50vh',
            overflowY: 'auto',
            '& > li:first-child': {
                // Round top corners when not searchable
                borderRadius: function (_a) {
                    var isSearchable = _a.isSearchable;
                    return !isSearchable ? '1px 1px 0 0' : undefined;
                },
            },
            '& > li:last-child': {
                // Round bottom corners
                borderRadius: '0 0 1px 1px',
            },
        },
    });
});
export var Listbox = function () {
    var _a = useComboboxContext(), comboboxProps = _a.comboboxProps, filteredOptions = _a.filteredOptions, components = _a.components, listboxHasFocus = _a.listboxHasFocus, isOptionSelected = _a.isOptionSelected, selectOption = _a.selectOption, deselectOption = _a.deselectOption, searchString = _a.searchString, setMenuIsOpen = _a.setMenuIsOpen;
    var id = comboboxProps.id, isMulti = comboboxProps.isMulti, strings = comboboxProps.strings, showSelectedOptions = comboboxProps.showSelectedOptions, isLoading = comboboxProps.isLoading, isSearchable = comboboxProps.isSearchable, ListboxProps = comboboxProps.ListboxProps;
    var MenuItem = components.MenuItem, NoResultsMenuItem = components.NoResultsMenuItem, LoadingResultsMenuItem = components.LoadingResultsMenuItem, MenuListSubheader = components.MenuListSubheader;
    var noResults = strings.noResults, loadingResults = strings.loadingResults;
    var inputRef = React.useRef(null);
    var classes = useStyles({ isSearchable: isSearchable });
    var _b = useListboxHandlers({
        hasFocus: listboxHasFocus,
        isMulti: isMulti,
        isOptionSelected: isOptionSelected,
        selectOption: selectOption,
        deselectOption: deselectOption,
        onEnter: function () {
            setMenuIsOpen(false);
        },
        onEscape: function () {
            var _a;
            setMenuIsOpen(false);
            if (inputRef.current) {
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        },
        onTab: function () {
            var _a;
            setMenuIsOpen(false);
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
    }), listRef = _b.listRef, handleOnMouseUp = _b.handleOnMouseUp, handleOnKeyDown = _b.handleOnKeyDown;
    function filterSelected(option) {
        return !(!showSelectedOptions && isOptionSelected(option));
    }
    function filterGroups(optionOrGroup) {
        if ('options' in optionOrGroup) {
            var groupOptions = optionOrGroup.options.filter(filterSelected);
            return groupOptions.length ? __assign(__assign({}, optionOrGroup), { options: groupOptions }) : [];
        }
        return filterSelected(optionOrGroup) ? optionOrGroup : [];
    }
    function renderOption(optionOrGroup, groupLabel) {
        var _a;
        if ('options' in optionOrGroup) {
            // The problem with using optionOrGroup.label is there could be a space in the label
            var subHeaderId = "combobox-subheader-".concat((_a = optionOrGroup.id) !== null && _a !== void 0 ? _a : optionOrGroup.label.replace(/ /g, ''));
            return (React.createElement("li", { key: subHeaderId, role: "presentation" },
                React.createElement("ul", { role: "group", "aria-labelledby": subHeaderId, className: classes.list },
                    React.createElement(MenuListSubheader, { id: subHeaderId, key: optionOrGroup.label, group: optionOrGroup, role: "presentation" }, optionOrGroup.label),
                    optionOrGroup.options.map(function (option) { return renderOption(option, optionOrGroup.label); }))));
        }
        var option = optionOrGroup;
        return (React.createElement(MenuItem, { key: "".concat(groupLabel, "-").concat(option.value), option: option, 
            // MUI (ButtonBase) calls onClick through onKeyDown without offering an escape hatch - Use onMouseUp to bypass.
            onMouseUp: function () { return handleOnMouseUp(option); }, onKeyDown: function (event) { return handleOnKeyDown(event, option); }, selected: isOptionSelected(option), "aria-selected": isOptionSelected(option) }, option.label));
    }
    return (React.createElement(BbUiListbox, __assign({ ref: listRef, id: "".concat(id, "-menu"), "aria-labelledby": "".concat(id, "-label"), "aria-multiselectable": isMulti }, ListboxProps, { 
        // Declare after spread to merge consumer styles with internal styles.
        className: clsx(classes.list, classes.listbox, ListboxProps === null || ListboxProps === void 0 ? void 0 : ListboxProps.className) }),
        isLoading && loadingResults && (React.createElement(LoadingResultsMenuItem, { role: "option" }, loadingResults)),
        !isLoading && !filteredOptions.length && searchString && (React.createElement(NoResultsMenuItem, { role: "option" }, noResults(searchString))),
        !isLoading && filteredOptions.flatMap(filterGroups).map(function (option) { return renderOption(option); })));
};
export default Listbox;
//# sourceMappingURL=Combobox.Listbox.js.map